// Purchase order list — /procurement. ERP expansion chunk 1, phase 6.
//
// Table + status tabs + search, matching the Admin/VendorsAdmin.tsx and
// Receiving/ReceivingHubPage.tsx patterns: useApi + useState/useEffect (no
// TanStack Query), a debounced free-text search, and a shared status-chip
// idiom. All API-shape assumptions live in poApi.ts.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import { formatEventDate } from '../utils/date-utils';
import PageHeader, { PrimaryAction, SecondaryAction } from '../components/PageHeader';
import PageLayout from '../components/ui/PageLayout';
import { EmptyState } from '../components/ui/EmptyState';
import { Progress } from '../components/ui/Progress';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { Tag } from '../components/ui/Tag';
import { CATEGORY } from '../components/ui/categoryColors';
import { Tooltip } from '../components/ui/Tooltip';
import { FilterBar, FilterSearch, FilterSelect, Segmented } from '../components/FilterBar';
import { fieldBase } from '../components/review/fieldStyles';
import EntityCombobox, { type ComboboxOption } from '../components/EntityCombobox';
import { DataTable, type DataTableHandle } from '../components/table/DataTable';
import ReorderPanel from './ReorderPanel';
import {
  usePoApi,
  centsToDollars,
  formatCents,
  PO_STATUS_LABELS,
  PO_OPEN_STATUSES,
  type PoListRow,
  type PoReturnsFilter,
  type PoStatus,
} from './poApi';

interface StatusTab {
  key: string;
  label: string;
  /** CSV passed to the API's ?status= filter. Absent = no filter (All). */
  statusParam?: string;
}

const STATUS_TABS: StatusTab[] = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft', statusParam: 'draft' },
  { key: 'pending_approval', label: 'Pending approval', statusParam: 'pending_approval' },
  { key: 'open', label: 'Open', statusParam: PO_OPEN_STATUSES.join(',') },
  { key: 'received', label: 'Received', statusParam: 'received' },
  { key: 'closed', label: 'Closed', statusParam: 'closed' },
  { key: 'cancelled', label: 'Cancelled', statusParam: 'cancelled' },
];

interface VendorOption {
  id: string;
  code: string | null;
  name: string;
  is_disabled: boolean;
}

/** The empty filter, spelled as an option so it can be chosen again. A picker
 * with no way back to "everything" is a filter you can only tighten. */
// A reset row, not a selection. Its id is deliberately NOT the empty string the
// filter holds at rest: matching would make the combobox print "All vendors" as
// a chosen value in the text color, and a field that looks filled in does not
// look like somewhere you can type. Unmatched, the field rests on its
// placeholder and opens on focus, which is the invitation.
const ALL_VENDORS_KEY = '__all__';
const ALL_VENDORS: ComboboxOption = { id: ALL_VENDORS_KEY, label: 'All vendors' };

const ProgressBar: React.FC<{ percent: number }> = ({ percent }) => (
  <Progress
    className="min-w-[110px]"
    size="md"
    label="Received"
    value={Math.max(0, Math.min(100, percent))}
    max={100}
    display="percent"
    success
    fillIn
  />
);

const STATUS_TONE: Record<PoStatus, { tone?: 'neutral' | 'info' | 'good' | 'warn' | 'bad' | 'accent'; hex?: string }> = {
  draft: { tone: 'neutral' },
  pending_approval: { tone: 'warn' },
  submitted: { tone: 'info' },
  partially_received: { tone: 'accent' },
  received: { tone: 'good' },
  closed: { hex: CATEGORY.indigo.ink.hex },
  cancelled: { tone: 'bad' },
};

const StatusChip: React.FC<{ status: PoStatus }> = ({ status }) => (
  <Tag {...STATUS_TONE[status]}>{PO_STATUS_LABELS[status]}</Tag>
);

/**
 * Stock that went back to the vendor, said in the status cell.
 *
 * ONE form, not two counts: a row that still has a claim open reads "1
 * awaiting", because the unanswered ones are the only ones anybody chases. Once
 * they are all answered it reads "2 returns", which is history worth seeing on
 * a list but not worth a second number beside it.
 *
 * Neutral tonal, never amber: a return is a normal thing that happened to an
 * order, and the status chip beside it is already carrying the color.
 */
const ReturnsChip: React.FC<{ open: number; settled: number }> = ({ open, settled }) => {
  const total = open + settled;
  if (total <= 0) return null;
  const awaiting = open > 0;
  return (
    <Tag variant="code" size="sm" className="ml-1.5">
      {awaiting ? `${open} awaiting` : `${total} return${total === 1 ? '' : 's'}`}
      {/* The chip's own words are short by design, so the sentence a reader
          hears is finished here rather than in a tooltip. */}
      <span className="sr-only">
        {awaiting ? ' a vendor answer on a return' : ' settled with the vendor'}
      </span>
    </Tag>
  );
};

const OriginBadge: React.FC<{ origin: PoListRow['origin'] }> = ({ origin }) => {
  if (origin === 'app') return null;
  const label = origin === 'backfill' ? 'Imported' : 'Imported (PDF)';
  return (
    <Tooltip label="History brought in from the old spreadsheets, still editable.">
      <Tag size="sm" className="ml-1.5">{label}</Tag>
    </Tooltip>
  );
};

const getRowId = (row: PoListRow) => String(row.id);

// Module-scope on purpose (the DataTable contract): every cell is pure over
// the row, so nothing here closes over page state. No `sortable` — the server
// owns the order (order_date desc), same as before the shared table.
//
// Every column carries a `meta.csv`, and it has to: with no `accessorFn`
// anywhere here (nothing sorts client-side), a column without one would be
// left out of the export entirely. What each returns is the value a
// spreadsheet wants, not the one the cell paints — the raw YYYY-MM-DD behind
// "Aug 27, 2026", the status LABEL behind the chip, dollars as a number behind
// "$1,234.50".
// One page of orders; the server caps at 200. Export CSV names this cap in
// its hint when the page is full.
const PO_LIST_PAGE_SIZE = 100;

const PO_COLUMNS: ColumnDef<PoListRow, any>[] = [
  {
    id: 'po_number',
    header: 'PO Number',
    meta: { cellClass: 'whitespace-nowrap', csv: (row) => row.po_number },
    cell: ({ row }) => (
      <>
        <Link
          to={`/procurement/po/${row.original.id}`}
          className="font-mono text-primary hover:underline"
        >
          {row.original.po_number}
        </Link>
        <OriginBadge origin={row.original.origin} />
      </>
    ),
  },
  {
    id: 'vendor',
    header: 'Vendor',
    // The em-dash the cell paints is a placeholder for the eye; the file gets
    // an empty cell, which is what a spreadsheet reads as "no vendor".
    meta: { cellClass: 'text-on-surface', csv: (row) => row.vendor_name || '' },
    cell: ({ row }) => row.original.vendor_name || '—',
  },
  {
    id: 'order_date',
    header: 'Order Date',
    // The stored YYYY-MM-DD, not "Aug 27, 2026": a sheet sorts the first and
    // treats the second as prose.
    meta: {
      numeric: true,
      cellClass: 'text-on-surface-variant whitespace-nowrap',
      csv: (row) => row.order_date,
    },
    cell: ({ row }) => formatEventDate(row.original.order_date, 'MMM D, YYYY') || '—',
  },
  {
    id: 'status',
    header: 'Status',
    // The same label the chip shows, off the same map, so the file and the
    // screen cannot come to say different words for one status.
    meta: { align: 'center', csv: (row) => PO_STATUS_LABELS[row.status] },
    cell: ({ row }) => (
      <span className="inline-flex items-center whitespace-nowrap">
        <StatusChip status={row.original.status} />
        <ReturnsChip
          open={row.original.returns_open || 0}
          settled={row.original.returns_settled || 0}
        />
      </span>
    ),
  },
  {
    id: 'progress',
    header: 'Progress',
    // The number behind the bar, 0-100.
    meta: { csv: (row) => row.percent_received },
    cell: ({ row }) => <ProgressBar percent={row.original.percent_received} />,
  },
  {
    id: 'subtotal',
    header: 'Subtotal',
    meta: {
      align: 'right',
      numeric: true,
      cellClass: 'text-on-surface',
      csv: (row) => centsToDollars(row.subtotal_cents),
    },
    cell: ({ row }) => formatCents(row.original.subtotal_cents),
  },
];

const PurchaseOrderList: React.FC = () => {
  const api = useApi();
  const poApi = usePoApi();
  const toast = useToast();

  const [rows, setRows] = useState<PoListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  // A link may arrive asking for a tab: the notification bell sends approvers
  // to /procurement?tab=pending_approval. Same pattern as the receiving hub's
  // ?q= — the param is an entrance, not state, so tab clicks do not write back.
  const location = useLocation();
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab && STATUS_TABS.some((t) => t.key === tab)) setActiveTab(tab);
  }, [location.search]);
  const [search, setSearch] = useState('');
  const [vendorId, setVendorId] = useState('');
  // '' is the filter at rest and sends nothing. Local like the status tabs and
  // the vendor picker: none of the three writes itself back to the URL, and a
  // single filter that did would be the odd one out.
  const [returnsFilter, setReturnsFilter] = useState<'' | PoReturnsFilter>('');
  const [vendors, setVendors] = useState<VendorOption[]>([]);

  // The Pending-approval tab's own count, on the bell's discipline: the
  // endpoint answers 0 to non-approvers, so the badge never lights for
  // someone who cannot act on it. Fetched alongside every rows fetch (below)
  // so the tab can never disagree with the table under it — two counts of one
  // question on one screen must move together. The bell keeps its own poll.
  const [approvalCount, setApprovalCount] = useState(0);
  const fetchApprovalCount = useCallback(async () => {
    try {
      const data = (await api.get('/procurement/approval-inbox-count')) as { count: number };
      setApprovalCount(data.count || 0);
    } catch {
      // A badge is decoration on a tab that works without it.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Table fade-in on initial load — one container-level transition, not a
  // per-row stagger (100 rows would take seconds to settle).
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const activeStatusParam = useMemo(
    () => STATUS_TABS.find((t) => t.key === activeTab)?.statusParam,
    [activeTab]
  );

  // Retired vendors stay in this list on purpose (?includeDisabled=1): their
  // purchase orders are still history somebody looks up. The sublabel is what
  // keeps the retired ones distinguishable now the name is on its own line.
  const vendorPickerOptions = useMemo<ComboboxOption[]>(
    () => [
      ALL_VENDORS,
      ...vendors.map((v) => ({
        id: v.id,
        label: v.name,
        chip: v.code,
        sublabel: v.is_disabled ? 'Retired' : null,
      })),
    ],
    [vendors]
  );

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await poApi.list({
        status: activeStatusParam,
        vendor_id: vendorId || undefined,
        q: search.trim() || undefined,
        returns: returnsFilter || undefined,
        page_size: PO_LIST_PAGE_SIZE,
      });
      setRows(data.rows || []);
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStatusParam, vendorId, search, returnsFilter]);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchRows();
      fetchApprovalCount();
    }, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [fetchRows, fetchApprovalCount, search]);

  useEffect(() => {
    (async () => {
      try {
        const data = (await api.get('/procurement/vendors?includeDisabled=1')) as {
          vendors: VendorOption[];
        };
        setVendors(data.vendors || []);
      } catch {
        // Vendor filter is a convenience — the page still works without it.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Export CSV asks the table for what it is showing, so the file is the
  // screen: these filters, this order, these columns. The hint says so out
  // loud, because the one thing a person cannot check after the download is
  // whether the filters came along.
  //
  // The list fetches one page. When it is full, the hint says "the first 100"
  // instead of "100": a file that leaves the app must not read as everything
  // when it is a page.
  const tableRef = useRef<DataTableHandle>(null);
  const atCap = rows.length >= PO_LIST_PAGE_SIZE;
  const tools = [
    {
      label: 'Export CSV',
      hint: atCap
        ? `the first ${rows.length} rows, current filters`
        : `${rows.length} row${rows.length === 1 ? '' : 's'}, current filters`,
      disabled: loading || rows.length === 0,
      onSelect: () => {
        // The count comes back from the export rather than from `rows`: it is
        // the number of lines actually written. null means the table was still
        // loading and wrote nothing.
        const count = tableRef.current?.exportCsv('purchase-orders');
        if (count == null) {
          toast.error('The list is still loading. Try again in a moment.');
          return;
        }
        toast.success(`Exported ${count} row${count === 1 ? '' : 's'}.`);
      },
    },
  ];

  return (
    <PageLayout width="wide" kind="workspace" className="pt-4 space-y-5">
      <PageHeader
        title="Purchase Orders"
        helpTitle="About purchase orders"
        lede="What Firefly ordered from a vendor: what, how many, at what price, and whether it has arrived."
        primary={<PrimaryAction to="/procurement/new">+ New Purchase Order</PrimaryAction>}
        actions={
          <>
            <SecondaryAction to="/procurement/scan">Scan PO</SecondaryAction>
            {/* Next to New Purchase Order rather than next to Scan PO's errand:
                this is a way of STARTING an order, not of filing one that already
                happened. */}
            <SecondaryAction to="/procurement/quote">From a quote</SecondaryAction>
            <SecondaryAction variant="text" to="/procurement/import">
              Import history
            </SecondaryAction>
          </>
        }
        tools={tools}
        help={
          <>
            <p>
              A purchase order is the record of what Firefly ordered from a vendor: what,
              how many, at what price, and whether it has arrived yet.
            </p>
            <p className="text-on-surface font-medium">The stages</p>
            <p>
              <span className="text-on-surface">Draft:</span> still being put together,
              nothing has been sent anywhere yet.
            </p>
            <p>
              <span className="text-on-surface">Pending approval:</span> the total is at or
              over the approval threshold ($2,500 unless an admin has changed it), so a
              person has to say yes before it goes to the vendor.
            </p>
            <p>
              <span className="text-on-surface">Submitted:</span> sent to the vendor,
              nothing has arrived yet.
            </p>
            <p>
              <span className="text-on-surface">Partially received / Received:</span> track
              what has actually shown up against what was ordered.
            </p>
            <p>
              <span className="text-on-surface">Closed / Cancelled:</span> done, either
              because everything Firefly cared about arrived (or it was time to stop
              chasing the rest) or because the order was called off.
            </p>
            <p className="text-on-surface font-medium">Rows marked &quot;Imported&quot;</p>
            <p>
              A few rows say Imported instead of moving through the usual stages. Those are
              older purchase orders brought in from the spreadsheets Firefly used before
              this page existed. They still work like any other order and can be edited if
              something needs correcting.
            </p>
          </>
        }
      />

      <FilterBar>
        <Segmented
          label="Which purchase orders to show"
          options={STATUS_TABS.map((t) => ({
            key: t.key,
            label: t.label,
            // The Pending-approval count rides the segment it belongs to. The
            // component hides it at zero and re-measures the thumb when it pops
            // in, which is why the page no longer owns that measurement.
            badge: t.key === 'pending_approval' ? approvalCount : undefined,
          }))}
          value={activeTab}
          onChange={setActiveTab}
        />
        {/* The label is the field's accessible name, and sr-only is how it can
            be both that and absent from a one-line filter bar.

            Fixed at 14rem rather than the content fit a select gets: the longest
            vendor name is 39 characters, and the filter bar should not reflow
            every time the pick changes. */}
        {/* A chevron, like the FilterSelects it sits beside: a combobox has none of
            its own, and without one the pill reads as a static chip. The label is
            sr-only, so the input fills the wrapper and the icon centers on it. */}
        <span className="relative inline-flex">
          <EntityCombobox
            label="Filter by vendor"
            labelClassName="sr-only"
            value={vendorId}
            options={vendorPickerOptions}
            onSelect={(o) => setVendorId(o && o.id !== ALL_VENDORS_KEY ? String(o.id) : '')}
            placeholder="All vendors"
            className="w-56"
            // The bar's pill, built from the shared field tone rather than a
            // second definition of it: this control and the search beside it
            // cannot disagree about fill or focus.
            inputClassName={`${fieldBase} w-full rounded-full pl-3 pr-8 py-1.5`}
            emptyMessage="No vendor matches that."
            noOptionsMessage="No vendors yet."
          />
          <svg aria-hidden="true" className="pointer-events-none absolute right-3 top-1/2 h-3 w-3 -translate-y-1/2 text-on-surface-variant" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
        {/* The returns axis, beside status rather than folded into it: a return
            is not a stage a PO passes through, and a closed order waiting on a
            vendor's answer is exactly the row somebody comes here to find. */}
        <FilterSelect
          label="Returns"
          value={returnsFilter}
          onChange={(v) => setReturnsFilter(v as '' | PoReturnsFilter)}
        >
          <option value="">Any</option>
          <option value="any">Has returns</option>
          <option value="awaiting">Awaiting resolution</option>
          <option value="refunded">Refunded</option>
          <option value="replaced">Replaced</option>
        </FilterSelect>
        <FilterSearch
          value={search}
          onChange={setSearch}
          label="Search purchase orders by number or vendor"
          placeholder="Search PO number or vendor…"
        />
      </FilterBar>

      {/* The mount fade is all this section still carries: the vessel (shell,
          strip, radius, scroll container) is the DataTable's. The results count
          moved into the strip, keeping its live region so a filter change is
          still announced. */}
      <section
        className={`transition-all duration-300 ease-out motion-reduce:transition-none motion-reduce:opacity-100 motion-reduce:translate-y-0 ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'
        }`}
      >
        <DataTable<PoListRow>
          ref={tableRef}
          data={rows}
          columns={PO_COLUMNS}
          getRowId={getRowId}
          stickyHeader
          strip={
            <span className="ml-auto text-xs text-on-surface-variant tabular-nums" role="status" aria-live="polite">
              {rows.length} order{rows.length === 1 ? '' : 's'}
            </span>
          }
          loading={loading}
          loadingContent={
            <SkeletonGroup label="Loading purchase orders" className="space-y-2 px-4 py-6">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
            </SkeletonGroup>
          }
          empty={
            <EmptyState variant="area">No purchase orders match the current filter.</EmptyState>
          }
        />
      </section>

      <ReorderPanel />
    </PageLayout>
  );
};

export default PurchaseOrderList;
